import React from 'react'
import classNames from 'classnames'
import useAnimateOutState from '../hooks/useAnimateOutState'
import { doesSkinHaveSlots } from '../products/productUtils'
import QuantityStepper from '../components/QuantityStepper'
import Button from '../components/Button'
import { IconChevDown } from '../components/icons'
import { InflatedListItemProduct, Room } from './ListTypes'
import DropdownSelector from '../components/DropdownSelector'
import { ListItemDetails } from './ListItemDetails'
import { ProductPriceAvailable, ProductPriceUnavailable } from '../productPricing/ProductPricingContext'
import { PriceTypeLabel } from './PriceTypeLabel'
import { AnalyticsContext } from '../analytics/AnalyticsContext'

interface Props {
  configuredProduct: InflatedListItemProduct
  isFocused: boolean
  onClickEdit: () => void
  onClickDuplicate: () => void
  onClickMore: () => void
  expanded: boolean
  onAddQuantity: (quantityDelta: number) => void
  onDelete: () => void
  onChangeNote: (newQuantity: string) => void
  onChangeRoom: (newRoomId: string | null) => void
  rooms: readonly Room[]
  showPrice: boolean
  price: ProductPriceAvailable | typeof ProductPriceUnavailable
  priceLoading?: boolean
  priceDisclaimer: string | null
  priceTypeLabel: PriceTypeLabel
  isConfigureDisabled: boolean
}

const ListItemRow: React.FunctionComponent<Props> = ({
  configuredProduct,
  onClickEdit,
  onAddQuantity,
  onDelete,
  onClickDuplicate,
  isFocused,
  onClickMore,
  expanded: shouldExpand,
  onChangeNote,
  onChangeRoom,
  rooms,
  price,
  priceDisclaimer,
  priceLoading,
  showPrice,
  priceTypeLabel,
  isConfigureDisabled
}) => {
  const analyticsLogger = React.useContext(AnalyticsContext)
  const [shouldRenderExpanded, isExpanded] = useAnimateOutState(shouldExpand, 350)

  const element = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    if (isFocused) {
      setTimeout(() => {
        element.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 300)
    }
  }, [isFocused])

  return (
    <div
      className={classNames('ListItemRow', { isFocused })}
      ref={element}
    >
      <ListItemDetails
        configuredProduct={configuredProduct}
        showPrice={showPrice}
        // isAuthenticated={isAuthenticated}
        priceLoading={priceLoading}
        price={price}
        priceDisclaimer={priceDisclaimer}
        priceTypeLabel={priceTypeLabel}
      />
      <div className="actions">
        <QuantityStepper
          value={configuredProduct.quantity}
          onAddQuantity={onAddQuantity}
        />
        <Button onClick={onClickMore}>
          <span style={{ minWidth: '40px', textAlign: 'left' }}>
            {shouldExpand ? 'Close' : 'More'}
          </span>
          <img
            src={IconChevDown}
            alt=""
            style={{
              transition: '200ms',
              transform: `rotate(${shouldExpand ? 0.5 : 0}turn)`
            }}
          />
        </Button>
      </div>
      {
        !shouldRenderExpanded && configuredProduct.note.trim().length > 0 && (
          <div className="note">
            {configuredProduct.note}
          </div>
        )
      }
      {
        shouldRenderExpanded && (
          <div className={classNames('expandingContent', { expanded: isExpanded })}>
            <DropdownSelector
              selected={configuredProduct.roomId ?? ''}
              onSelect={option => { onChangeRoom(option?.key || null) }}
              options={[
                { key: '', label: 'No room' },
                ...rooms.map(room => (
                  { key: room.uid, label: room.name }
                ))
              ]}
            />
            <textarea
              onChange={e => onChangeNote(e.target.value)}
              value={configuredProduct.note}
              placeholder="Add your notes here"
            />
            <div className=" row">
              <Button
                onClick={() => {
                  analyticsLogger.log({
                    event: 'cta_click',
                    cta_name: 'Customise',
                    cta_link: null,
                    cta_category: 'Button',
                    cta_location: 'My list'
                  })

                  onClickEdit()
                }}
                disabled={
                  (configuredProduct.skinProduct && !doesSkinHaveSlots(configuredProduct.skinProduct))
                  || isConfigureDisabled
                }
              >
                Customise
              </Button>
              <Button
                onClick={() => {
                  analyticsLogger.log({
                    event: 'cta_click',
                    cta_name: 'Make a copy',
                    cta_link: null,
                    cta_category: 'Button',
                    cta_location: 'My list'
                  })

                  onClickDuplicate()
                }}
              >
                Make a copy
              </Button>
              <Button
                buttonStyle="redStroke"
                onClick={() => {
                  analyticsLogger.log({
                    event: 'cta_click',
                    cta_name: 'Remove',
                    cta_link: null,
                    cta_category: 'Button',
                    cta_location: 'My list'
                  })

                  onDelete()
                }}
              >
                Remove
              </Button>
            </div>
          </div>
        )
      }
    </div>
  )
}

export default ListItemRow
